import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import EditText from "../../component/ui/EditText";
import TextArea from "../../component/ui/TextArea";
import Button from "../../component/ui/Button";
// Ensure ScrollTrigger is registered before use
gsap.registerPlugin(ScrollTrigger);
const ContactForm = () => {
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    message: "",
  });

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event?.target?.value,
    }));
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    // Handle form submission logic here
  };

  useEffect(() => {
    const context = gsap.context(() => {
      // --- 1. Scrubbed Text Reveal Animation (Merise.ae style) ---
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content"),
      );

      textElements.forEach((element) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            // Use the parent card for triggering
            trigger: element.closest(".animated-card-container"),
            start: "top 85%", // Animation starts when the card enters view
            end: "center 60%", // Animation ends when the card is near the center
            scrub: 1, // Scroll से smooth link (scrubbing)
            // markers: true,
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        tl.to(element, {
          y: 0, // Final state: Original position
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out", // Thoda smooth movement
          // We don't need a stagger here as it applies to each text element individually
        });
      });

      // --- 2. Scrubbed Card Fade-In/Translate Animation (Alternative to Divider) ---
      // This applies a reveal animation to the cards themselves
      const cardElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-card-container"),
      );

      cardElements.forEach((card) => {
        // Initial State: Slightly down and transparent
        gsap.set(card, {
          y: 50,
          opacity: 0,
        });

        gsap.to(card, {
          y: 0,
          opacity: 1, // Target state is fully visible
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 95%", // Animation starts when card is almost in view
            end: "center 70%",
            scrub: 1,
            // markers: true,
          },
        });
      });
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <section
      className="w-full max-w-[1612px] mx-auto px-4 sm:px-6 lg:px-8 mb-[50px]"
      ref={sectionRef}
    >
      <div className="flex flex-col gap-[40px] md:gap-[60px] lg:gap-[80px] justify-start items-center w-full">
        {/* Section Title */}
        <h2
          className="text-[36px] sm:text-[45px] md:text-[52px] lg:text-[60px] font-bold leading-[45px] sm:leading-[57px] md:leading-[66px] lg:leading-[76px] text-center animated-text-content"
          style={{ fontFamily: "Sora" }}
        >
          Contact Us
        </h2>

        {/* Contact Form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-[20px] md:gap-[30px] lg:gap-[40px] justify-start items-center w-full"
        >
          <div className="flex flex-col gap-[20px] md:gap-[30px] lg:gap-[40px] justify-start items-center w-full">
            {/* First Row - First Name and Last Name */}
            <div className="flex flex-col md:flex-row gap-[15px] md:gap-[22px] lg:gap-[30px] justify-start items-center w-full">
              <EditText
                placeholder="First Name"
                value={formData?.firstName}
                onChange={handleInputChange("firstName")}
                text_font_size="30"
                text_font_family="Sora"
                text_font_weight="400"
                text_line_height="18px"
                text_color="##1C233A4D"
                border_border="1px solid #1C233A"
                border_border_radius="50px"
                padding="7px 15px"
                className="w-full text-[14px] sm:text-[17px] 2xl:text-[30px] leading-[18px] sm:leading-[22px] 2xl:leading-[26px] py-[15px] px-[15px] sm:py-[20px] sm:px-[22px] 2xl:py-[28px] 2xl:px-[40px]"
              />
              <EditText
                placeholder="Last Name"
                value={formData?.lastName}
                onChange={handleInputChange("lastName")}
                text_font_size="30"
                text_font_family="Sora"
                text_font_weight="400"
                text_line_height="18px"
                text_color="##1C233A4D"
                border_border="1px solid #1C233A"
                border_border_radius="50px"
                padding="7px 15px"
                className="w-full text-[14px] sm:text-[17px] 2xl:text-[30px] leading-[18px] sm:leading-[22px] 2xl:leading-[26px] py-[15px] px-[15px] sm:py-[20px] sm:px-[22px] 2xl:py-[28px] 2xl:px-[40px]"
              />
            </div>

            {/* Second Row - Email and Mobile */}
            <div className="flex flex-col md:flex-row gap-[15px] md:gap-[22px] lg:gap-[30px] justify-start items-center w-full">
              <EditText
                placeholder="Email"
                type="email"
                value={formData?.email}
                onChange={handleInputChange("email")}
                text_font_size="30"
                text_font_family="Sora"
                text_font_weight="400"
                text_line_height="18px"
                text_color="##1C233A4D"
                border_border="1px solid #1C233A"
                border_border_radius="50px"
                padding="7px 15px"
                className="w-full text-[14px] sm:text-[17px] 2xl:text-[30px] leading-[18px] sm:leading-[22px] 2xl:leading-[26px] py-[15px] px-[15px] sm:py-[20px] sm:px-[22px] 2xl:py-[28px] 2xl:px-[40px]"
              />
              <EditText
                placeholder="Mobile No."
                type="tel"
                value={formData?.mobile}
                onChange={handleInputChange("mobile")}
                text_font_family="Sora"
                text_font_weight="400"
                text_line_height="18px"
                text_color="##1C233A4D"
                border_border="1px solid #1C233A"
                border_border_radius="50px"
                padding="7px 15px"
                className="w-full text-[14px] sm:text-[17px] 2xl:text-[30px] leading-[18px] sm:leading-[22px] 2xl:leading-[26px] py-[15px] px-[15px] sm:py-[20px] sm:px-[22px] 2xl:py-[28px] 2xl:px-[40px]"
              />
            </div>
          </div>

          {/* Message TextArea */}
          <TextArea
            placeholder="Message"
            value={formData?.message}
            text_color="##1C233A4D"
            onChange={handleInputChange("message")}
            className="w-full"
            rows={6}
            padding=" py-[15px] px-[15px] sm:py-[20px] sm:px-[22px] 2xl:py-[28px] 2xl:px-[40px] rounded-[20px] 2xl:rounded-[50px]"
          />

          {/* Submit Button */}
          <Button
            text="Submit"
            text_color="#ffffff"
            fill_background_color="#1C233A"
            text_font_size="24px"
            border_border_radius="50px"
            className=" px-[17px] py-[6px] sm:px-[25px] sm:py-[9px] md:px-[29px] md:py-[15px] lg:px-[54px] lg:py-[12px] "
            onClick={handleSubmit}
          />
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
